/**
 * The four places an add can go. Each keeps the same color and picture
 * everywhere it shows up: on the iPad, on the office pages and in Close Out
 * (approved mockup legend).
 */
import { Building2, ClipboardList, Lock, MapPin, Wrench, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { FieldAddItem, FieldTarget } from "./field-add-api";

export interface Place {
    target: FieldTarget;
    label: string;
    /** The line under the tile. */
    sub: string;
    Icon: LucideIcon;
    /** Text/icon color. */
    ink: string;
    /** Picked tile: soft fill + strong border. */
    on: string;
    /** Small pill (office rows, counts). */
    pill: string;
}

export const PLACES: Record<FieldTarget, Place> = {
    job: {
        target: "job",
        label: "This job",
        sub: "Today's visit only",
        Icon: ClipboardList,
        ink: "text-blue-700 dark:text-blue-300",
        on: "border-blue-700 bg-blue-50 dark:border-blue-400 dark:bg-blue-500/15",
        pill: "bg-blue-50 text-blue-800 dark:bg-blue-500/15 dark:text-blue-200",
    },
    device: {
        target: "device",
        label: "Device",
        sub: "Pick which one next ›",
        Icon: Wrench,
        ink: "text-orange-700 dark:text-orange-300",
        on: "border-orange-700 bg-orange-50 dark:border-orange-400 dark:bg-orange-500/15",
        pill: "bg-orange-50 text-orange-800 dark:bg-orange-500/15 dark:text-orange-200",
    },
    location: {
        target: "location",
        label: "This location",
        sub: "Every visit here",
        Icon: MapPin,
        ink: "text-green-700 dark:text-green-300",
        on: "border-green-700 bg-green-50 dark:border-green-400 dark:bg-green-500/15",
        pill: "bg-green-50 text-green-800 dark:bg-green-500/15 dark:text-green-200",
    },
    customer: {
        target: "customer",
        label: "This customer",
        sub: "All their locations",
        Icon: Building2,
        ink: "text-purple-700 dark:text-purple-300",
        on: "border-purple-700 bg-purple-50 dark:border-purple-400 dark:bg-purple-500/15",
        pill: "bg-purple-50 text-purple-800 dark:bg-purple-500/15 dark:text-purple-200",
    },
};

/** "4" Double Check · SN 40613G" */
export function deviceName(d: {
    label?: string | null;
    deviceLabel?: string | null;
    serial?: string | null;
    deviceSerial?: string | null;
}) {
    const label = d.label ?? d.deviceLabel ?? "Device";
    const serial = d.serial ?? d.deviceSerial ?? null;
    return serial ? `${label} · SN ${serial}` : label;
}

/** Where it landed, in plain words (Saved screen, office rows). */
export function whereLine(
    item: Pick<FieldAddItem, "target" | "deviceLabel" | "deviceSerial" | "deviceLocation">,
): string {
    switch (item.target) {
        case "device":
            return `On device ${deviceName(item)}${item.deviceLocation ? `, ${item.deviceLocation}` : ""}`;
        case "location":
            return "On this location, for every visit";
        case "customer":
            return "On this customer, for all their locations";
        default:
            return "On this job, for today's visit";
    }
}

/** Who will see it (Saved screen). */
export function whoSees(target: FieldTarget): string {
    switch (target) {
        case "device":
            return "The office and every tech who works on this device will see it.";
        case "location":
            return "The office and every tech who comes here will see it, at the top of the job.";
        case "customer":
            return "The office and every tech at this customer's locations will see it.";
        default:
            return "The office will see it on this job.";
    }
}

export function PlacePill({ item, className }: { item: FieldAddItem; className?: string }) {
    const p = PLACES[item.target];
    const text =
        item.target === "device"
            ? deviceName(item)
            : item.target === "location"
              ? (item.siteName ?? "Location")
              : item.target === "customer"
                ? (item.customerName ?? "Customer")
                : "This job";
    return (
        <span
            className={cn(
                "inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold",
                p.pill,
                className,
            )}
        >
            <p.Icon className="h-3 w-3 shrink-0" aria-hidden />
            <span className="truncate">{text}</span>
        </span>
    );
}

export function InsideOnlyLine({ children }: { children?: React.ReactNode }) {
    return (
        <div className="flex items-center gap-2 rounded-lg bg-amber-100 px-3 py-2 text-[13px] font-bold text-amber-900 dark:bg-amber-500/15 dark:text-amber-200">
            <Lock className="h-4 w-4 shrink-0" aria-hidden />
            <span>{children ?? "Inside only. Never goes to the customer."}</span>
        </div>
    );
}

export function shortDate(iso: string): string {
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`;
}

export function shortTime(iso: string): string {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
